// © 2026 AttainIP Engineering

export type Tier = 'alpha' | 'beta' | 'zeta';

/**
 * Tier pill for the Service Health table (RFC-0010).
 *
 * Renders the canonical alpha/beta/zeta classification reported by each
 * service's /admin/env endpoint. Colour mapping mirrors traffic-light
 * intent: green for dev (safe), amber for test, red for production
 * (acts on real data).
 *
 * Pass the optional `rawEnv` to show the underlying SERVICE_ENV value
 * as a tooltip (useful when an env alias like "production" / "prod"
 * resolves to "zeta").
 */
export function TierBadge({ tier, rawEnv }: { tier?: string | null; rawEnv?: string | null }) {
  if (!tier) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const normalised = tier.toLowerCase() as Tier;
  const style =
    normalised === 'alpha'
      ? 'bg-green-50 text-green-700 ring-green-600/20'
      : normalised === 'beta'
      ? 'bg-amber-50 text-amber-700 ring-amber-600/20'
      : normalised === 'zeta'
      ? 'bg-red-50 text-red-700 ring-red-600/20'
      : 'bg-gray-50 text-gray-600 ring-gray-500/20';

  const title = rawEnv && rawEnv !== normalised ? `SERVICE_ENV=${rawEnv}` : undefined;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ring-1 ring-inset ${style}`}
      title={title}
    >
      {normalised}
    </span>
  );
}
